// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

export const AuthContext = createContext();

const TOKEN_EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    setUser(null);
  };

  const readStoredSession = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    try {
      const decoded = jwtDecode(token);
      const tokenExpiry = decoded?.exp ? decoded.exp * 1000 : null;
      const storedExpiry = Number(localStorage.getItem('tokenExpiration')) || null;
      const expiresAt = tokenExpiry || storedExpiry;

      if (expiresAt && Date.now() >= expiresAt) {
        return null;
      }

      return { token, decoded, expiresAt };
    } catch (error) {
      console.error('Invalid token:', error);
      return null;
    }
  };

  const login = (token) => {
    const decoded = jwtDecode(token);
    const expirationTime = decoded?.exp
      ? decoded.exp * 1000
      : Date.now() + TOKEN_EXPIRATION_TIME;

    localStorage.setItem('token', token);
    localStorage.setItem('tokenExpiration', expirationTime.toString());

    setUser(decoded);
    setIsLoading(false);
  };

  const logout = () => {
    clearSession();
    setIsLoading(false);
    navigate('/login', { replace: true });
  };

  const checkTokenExpiration = () => {
    const session = readStoredSession();
    if (!session && localStorage.getItem('token')) {
      logout();
      return;
    }

    if (session?.decoded) {
      setUser(session.decoded);
    }
  };

  useEffect(() => {
    const session = readStoredSession();

    if (session?.decoded) {
      setUser(session.decoded);
      if (session.expiresAt) {
        localStorage.setItem('tokenExpiration', String(session.expiresAt));
      }
    } else {
      clearSession();
    }

    setIsLoading(false);

    const interval = setInterval(checkTokenExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: Boolean(user),
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
